/**
 * Data Source API Service
 *
 * Service functions for data source API operations.
 */

import { apiGet, apiPost, apiPut, apiDelete } from '../client';
import type {
  DataSourceTypesResponseApi,
  DataSourceTypeWithInstancesResponseApi,
  CreateDataSourceRequestApi,
  UpdateDataSourceRequestApi,
  DataSourceResponseApi,
  DataSourceListResponseApi,
  TriggerRefreshResponseApi,
  RefreshStatusResponseApi,
  UpdateRefreshScheduleRequestApi,
  RefreshScheduleResponseApi,
  UpdateRefreshScheduleResponseApi,
  NominalCategoryLabelsResponseApi,
  RefreshHistoryResponseApi,
  MappingConfigResponseApi,
  UpdateMappingConfigRequestApi,
  RecentSyncsResponseApi,
} from '../models/dataSourceApi';

/**
 * Fetches all available data source types from the API.
 * @returns Promise with the list of data source types
 */
export async function getDataSourceTypes(): Promise<DataSourceTypesResponseApi> {
  return apiGet<DataSourceTypesResponseApi>('data-sources/types');
}

/**
 * Creates a new data source via the API.
 * @param request - The create data source request
 * @returns Promise with the created data source
 */
export async function createDataSource(
  request: CreateDataSourceRequestApi
): Promise<DataSourceResponseApi> {
  return apiPost<DataSourceResponseApi>('data-sources', request);
}

/**
 * Fetches a data source type by ID with its available instances.
 * @param typeId - The data source type ID (e.g., 'af0', 'adc0')
 * @returns Promise with the data source type and its instances
 */
export async function getDataSourceTypeWithInstances(
  typeId: string
): Promise<DataSourceTypeWithInstancesResponseApi> {
  return apiGet<DataSourceTypeWithInstancesResponseApi>(`data-sources/types/${typeId}`);
}

/**
 * Fetches all data sources for the current tenant.
 * @returns Promise with the list of data sources
 */
export async function getAllDataSources(): Promise<DataSourceListResponseApi> {
  return apiGet<DataSourceListResponseApi>('data-sources');
}

/**
 * Fetches a single data source by ID with full configuration.
 * @param dataSourceId - The ID of the data source
 * @returns Promise with the data source details including configuration
 */
export async function getDataSourceById(
  dataSourceId: number
): Promise<DataSourceResponseApi> {
  return apiGet<DataSourceResponseApi>(`data-sources/${dataSourceId}`);
}

/**
 * Updates an existing data source via the API.
 * @param dataSourceId - The ID of the data source to update
 * @param request - The update data source request
 * @returns Promise with the updated data source
 */
export async function updateDataSource(
  dataSourceId: number,
  request: UpdateDataSourceRequestApi
): Promise<DataSourceResponseApi> {
  return apiPut<DataSourceResponseApi>(`data-sources/${dataSourceId}`, request);
}

/**
 * Deletes a data source by ID via the API.
 * @param dataSourceId - The ID of the data source to delete
 * @returns Promise that resolves when the data source is deleted
 */
export async function deleteDataSource(dataSourceId: number): Promise<void> {
  return apiDelete<void>(`data-sources/${dataSourceId}`);
}

/**
 * Triggers a manual refresh for a data source.
 * @param dataSourceId - The ID of the data source to refresh
 * @returns Promise with the trigger refresh response including refreshId
 */
export async function triggerDataSourceRefresh(
  dataSourceId: number
): Promise<TriggerRefreshResponseApi> {
  return apiPost<TriggerRefreshResponseApi>(`data-sources/${dataSourceId}/refresh`);
}

/**
 * Gets the current refresh status for a data source.
 * @param dataSourceId - The ID of the data source
 * @returns Promise with the refresh status including current and last successful refresh
 */
export async function getDataSourceRefreshStatus(
  dataSourceId: number
): Promise<RefreshStatusResponseApi> {
  return apiGet<RefreshStatusResponseApi>(`data-sources/${dataSourceId}/refresh/status`);
}

/**
 * Gets the refresh schedule for a data source.
 * @param dataSourceId - The ID of the data source
 * @returns Promise with the refresh schedule details and capabilities
 */
export async function getRefreshSchedule(
  dataSourceId: number
): Promise<RefreshScheduleResponseApi> {
  return apiGet<RefreshScheduleResponseApi>(`data-sources/${dataSourceId}/schedule`);
}

/**
 * Updates the refresh schedule for a data source.
 * @param dataSourceId - The ID of the data source
 * @param request - The update schedule request with cron expression and time window
 * @returns Promise with the updated schedule details
 */
export async function updateRefreshSchedule(
  dataSourceId: number,
  request: UpdateRefreshScheduleRequestApi
): Promise<UpdateRefreshScheduleResponseApi> {
  return apiPut<UpdateRefreshScheduleResponseApi>(`data-sources/${dataSourceId}/schedule`, request);
}

/**
 * Fetches nominal category labels from the Financials API for a given instance.
 * @param instanceId - The Financials instance ID
 * @returns Promise with the nominal category labels (category1-category8)
 */
export async function getNominalCategoryLabels(
  instanceId: string
): Promise<NominalCategoryLabelsResponseApi> {
  return apiGet<NominalCategoryLabelsResponseApi>(`data-sources/nominal-category-labels?instanceId=${encodeURIComponent(instanceId)}`);
}

/**
 * Fetches the refresh history for a data source.
 * @param dataSourceId - The ID of the data source
 * @param pageNumber - Page number (defaults to 1)
 * @param pageSize - Number of items per page (defaults to 10)
 * @returns Promise with the paginated refresh history
 */
export async function getDataSourceRefreshHistory(
  dataSourceId: number,
  pageNumber: number = 1,
  pageSize: number = 10
): Promise<RefreshHistoryResponseApi> {
  return apiGet<RefreshHistoryResponseApi>(
    `data-sources/${dataSourceId}/refresh/history?pageNumber=${pageNumber}&pageSize=${pageSize}`
  );
}

/**
 * Fetches the mapping configuration (transaction rules) for a data source.
 * @param dataSourceId - The ID of the data source
 * @returns Promise with the mapping config including transaction rules grouped by type
 */
export async function getMappingConfig(
  dataSourceId: number
): Promise<MappingConfigResponseApi> {
  return apiGet<MappingConfigResponseApi>(`data-sources/${dataSourceId}/mapping`);
}

/**
 * Updates the mapping configuration (transaction rules) for a data source.
 * @param dataSourceId - The ID of the data source
 * @param request - The update mapping config request with transaction rules
 * @returns Promise with the updated mapping config
 */
export async function updateMappingConfig(
  dataSourceId: number,
  request: UpdateMappingConfigRequestApi
): Promise<MappingConfigResponseApi> {
  return apiPut<MappingConfigResponseApi>(`data-sources/${dataSourceId}/mapping`, request);
}

/**
 * Fetches the most recent completed data source syncs across all sources.
 * @returns Promise with the list of recent sync items (up to 10)
 */
export async function getRecentSyncs(): Promise<RecentSyncsResponseApi> {
  return apiGet<RecentSyncsResponseApi>('data-sources/recent-syncs');
}
